import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Rating } from '@codex/basics';
import { Icon } from '@codex/icons';

import TableCellWithLink from './TableCellWithLink';


const GridTable = styled.table`
  table-layout: fixed;
  width: 100%;
  box-sizing: border-box;

  td:last-child  {
    border-right: none;
  }
  
  td {
    border-right: 1px solid #e5e5e5;
  }
`;

const ImagesTableCell = styled(TableCellWithLink)`
  vertical-align: center;
  text-align: center;
  padding-bottom: 10px;
  
  img {
    max-width: 90%;
  }
`;

const LinkTableCell = styled(TableCellWithLink)`
  padding-bottom: 5px;
  
  & > a {
    font-size: 1.02em;
    color: #4855a0;
    :hover {
      color: #4855a0;
    }
  }
`;

const RatingTableCell = styled(TableCellWithLink)`
  padding-bottom: 5px;
  text-align: center;
`;

const DescriptionTableCell = styled(TableCellWithLink)`
  padding-bottom: 5px
`;

const PriceTableCell = styled(TableCellWithLink)`
  padding-bottom: 5px;
  text-align: center;
  
  & > a > div {
    border-top: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
    padding: 7px 0 7px 0;
    font-weight: bold;
    font-size: 22px;
    color: #222222;
  }
`;

const AvailabilitiesTableCell = styled(TableCellWithLink)`
  padding-bottom: 5px;
  padding-top: 5px;
  
  span.availabilitiesLabel {
    color: #666666;
    font-size: 14px;
    @media ${({ theme }) => theme.breakpoints.desktop} {
      font-size: 13px;
    }
  }
`;

const LastRowForBorder = styled.tr`
  & > td {
    border-right: none;
    div {
      height: 10px;
      margin: 0 10px 10px 10px;
      border-bottom: 1px solid #e5e5e5;
    }
  }
`;

const GreenTickIcon = styled(Icon)`
  color: #488020;
  height: 24px;
  width: 24px;
  margin-right: 5px;
`;

const GreyCrossIcon = styled(Icon)`
  color: #cccccc;
  height: 24px;
  width: 24px;
  margin-right: 5px;
`;

const RichRelevanceProductsSingleRow = ({ productList, showBordersOnBottom }) => (
  <GridTable>
    <tbody>
      <tr>
        {productList.map((product) => (
          <ImagesTableCell linkURL={product.clickBeaconURL ? product.clickBeaconURL : product.linkURL} key={product.id}>
            {product.impressionBeaconURL && (
              <img src={product.impressionBeaconURL} alt={product.impressionBeaconURL} />
            )}
            <img src={product.imageURL} alt={product.name} />
          </ImagesTableCell>
        ))}
      </tr>
      <tr>
        {productList.map((product) => (
          <LinkTableCell linkURL={product.clickBeaconURL ? product.clickBeaconURL : product.linkURL} key={product.id}>
            <span dangerouslySetInnerHTML={{ __html: product.name }} />
          </LinkTableCell>
        ))}
      </tr>
      <tr>
        {productList.map((product) => (
          <RatingTableCell linkURL={product.linkURL} key={product.id}>
            <Rating maxCount={5} ratingValue={product.averageScore} ratingCount={product.numberOfReviews} />
          </RatingTableCell>
        ))}
      </tr>
      <tr>
        {productList.map((product) => (
          <DescriptionTableCell linkURL={product.clickBeaconURL ? product.clickBeaconURL : product.linkURL} key={product.id}>
            <ul>
              {product.productDescription.split('§')
                .map((descriptionItem) => (<li key={descriptionItem}>{descriptionItem}</li>))}
            </ul>
          </DescriptionTableCell>
        ))}
      </tr>
      <tr>
        {productList.map((product) => (
          <PriceTableCell linkURL={product.clickBeaconURL ? product.clickBeaconURL : product.linkURL} key={product.id}>
            <div>
              {product.price}
            </div>
          </PriceTableCell>
        ))}
      </tr>
      <tr>
        {productList.map((product) => (
          <AvailabilitiesTableCell
            linkURL={product.clickBeaconURL ? product.clickBeaconURL : product.linkURL}
            key={product.id}
            showBordersOnBottom={showBordersOnBottom}
          >
            <div className={'homeAvailability'}>
              {product.homeDelivery.available
                ? <GreenTickIcon size={'large'} icon={'Tick'} />
                : <GreyCrossIcon size={'large'} icon={'Cross'} />}
              <span className={'availabilitiesLabel'}>{product.homeDelivery.label}</span>
            </div>
            <div className={'storeAvailability'}>
              {product.availableInStore.available
                ? <GreenTickIcon size={'large'} icon={'Tick'} />
                : <GreyCrossIcon size={'large'} icon={'Cross'} />}
              <span className={'availabilitiesLabel'}>{product.availableInStore.label}</span>
            </div>
          </AvailabilitiesTableCell>
        ))}
      </tr>
      {showBordersOnBottom && (
        <LastRowForBorder>
          {productList.map((product) => (
            <td key={product.id}>
              <div />
            </td>
          ))}
        </LastRowForBorder>
      )}
    </tbody>
  </GridTable>
);

RichRelevanceProductsSingleRow.propTypes = {
  productList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    linkURL: PropTypes.string,
    imageURL: PropTypes.string,
    numberOfReviews: PropTypes.number,
    averageScore: PropTypes.number,
    productDescription: PropTypes.string,
    price: PropTypes.string,
    availableInStore: PropTypes.shape({
      available: PropTypes.bool,
      label: PropTypes.string
    }).isRequired,
    homeDelivery: PropTypes.shape({
      available: PropTypes.bool,
      label: PropTypes.string
    }).isRequired,
    clickBeaconURL: PropTypes.string,
    impressionBeaconURL: PropTypes.string
  })).isRequired,
  showBordersOnBottom: PropTypes.bool
};

RichRelevanceProductsSingleRow.defaultProps = {
  showBordersOnBottom: false
};

export default RichRelevanceProductsSingleRow;
